import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'common/hooks/state';
import { RootState } from 'common/store/store';
import { clearCredentials, setCredentials, markValidated } from 'features/login/authSlice';
import { Loader } from 'common/designSystem';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Purchases as PurchasesWeb } from '@revenuecat/purchases-js';
import { Mixpanel } from 'common/utils/mixpanel';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';
import ShepherdProvider from 'features/Shepherd/ShepherdProvider';
import NotificationPrimer from 'features/NotificationPrimer/NotificationPrimer';
import RatingPrompt from 'features/RatingPrompt/RatingPrompt';
import { initPushRegistration, consumePendingPath, OPEN_DOCUMENT_EVENT } from 'common/utils/pushRegistration';
import { safeStorage } from 'common/utils/safeStorage';
import { oneSignalLogin } from 'common/utils/onesignal';
import { syncAndroidPurchases, reportPurchasesLoginFailure } from 'features/pricing/syncPurchases';
import { useValidateTokenMutation } from './PersistLoginService';

// A validate that landed within this window is treated as still fresh, so a
// PersistLogin mount that immediately follows Welcome's validate (the boot
// handoff to /dashboard) doesn't fire a second, redundant validate-token.
const VALIDATE_FRESHNESS_MS = 60_000;

export const PersistLogin = () => {
  const dispatch = useAppDispatch();
  const { token, persist, lastValidatedAt } = useAppSelector((state: RootState) => state.auth);
  const [auth, setAuth] = useState(false);
  const [validateToken] = useValidateTokenMutation();
  const navigate = useNavigate();
  // Once-guard mirroring Welcome's hasAttemptedAuth: this layout route now
  // stays mounted across authed navigation, but a remount (StrictMode, route
  // transition edge cases) must never re-fire validate-token.
  const hasAttemptedAuth = useRef(false);

  useEffect(() => {
    if (hasAttemptedAuth.current) return;
    hasAttemptedAuth.current = true;

    const checkToken = async () => {
      let currentToken = token;

      // If no token in Redux, check Capacitor Preferences
      if (!currentToken && Capacitor.isNativePlatform()) {
        const { value } = await Preferences.get({ key: 'userToken' });
        if (value) {
          currentToken = { token: value };
        }
      }

      if (!currentToken) {
        // No token, redirect to login
        dispatch(clearCredentials());
        navigate('/login', { replace: true });
      } else if (lastValidatedAt && Date.now() - lastValidatedAt < VALIDATE_FRESHNESS_MS) {
        // A validate already succeeded within the freshness window (typically
        // Welcome's, moments before this mount on the /dashboard handoff).
        // Adopt the existing session and render immediately — same outcome as
        // the success path, minus the redundant token rotation.
        setAuth(true);
      } else {
        const retryAttempts = 1;

        const validateWithRetry = async () => {
          let attempts = 0;
          const attemptValidation = async () => {
            try {
              const res = await validateToken({ isPersist: true }).unwrap();
              const { data } = res;
              const payload = {
                user: data,
                token: { token: data.token },
                persist: { isPersist: persist ? persist.isPersist : false },
                shareToken: data.share_token,
              };
              dispatch(setCredentials(payload));
              // Stamp so a sibling mount (or a Welcome→PersistLogin race the
              // other way) can dedupe against this successful validate.
              dispatch(markValidated());

              // Re-identify user with RevenueCat on session restore
              try {
                if (Capacitor.isNativePlatform()) {
                  if (data.id) {
                    await Purchases.logIn({ appUserID: data.id.toString() });
                    oneSignalLogin(data.id);
                    // Android: post any Play purchase RevenueCat has not seen
                    // under the account id. Non-blocking; never throws.
                    syncAndroidPurchases('persist_login');
                  }
                } else if (data.id && PurchasesWeb.isConfigured()) {
                  const purchases = PurchasesWeb.getSharedInstance();
                  await purchases.identifyUser(data.id.toString());
                  safeStorage.setItem('rc_user_id', data.id.toString());
                  // Keep localStorage coherent with the verify-time merge:
                  // once the session belongs to an identified user, no stale
                  // anonymous id should survive (app.tsx would otherwise
                  // still see it after an rc_user_id-less boot).
                  safeStorage.removeItem('rc_anonymous_id');
                }
                if (data.id) {
                  Mixpanel.aliasOnce(data.id.toString());
                  Mixpanel.identify(data.id.toString());
                }
              } catch (rcError) {
                reportPurchasesLoginFailure(rcError, data.id, 'persist_login');
              }

              setAuth(true);
            } catch (err) {
              attempts += 1;
              if (attempts < retryAttempts) {
                await attemptValidation();
                return;
              }
              // Only clear credentials on a definite auth failure. Network /
              // server errors should leave the existing token in place so the
              // user can still try to use the app — a network blip during cold
              // launch shouldn't sign them out.
              const status = (err as { status?: number | string } | undefined)?.status;
              if (status === 401 || status === 403) {
                trackEvent(MixpanelEventName.sessionExpired, { cold_start: true, status });
                dispatch(clearCredentials());
                navigate('/login', { replace: true });
              } else {
                console.error('validateToken transient failure, keeping session:', err);
                setAuth(true);
              }
            }
          };

          await attemptValidation();
        };

        validateWithRetry();
      }
    };

    checkToken();
  }, []);

  useEffect(() => {
    if (!auth) return;
    // Register this device for push once the session is confirmed. Fire and
    // forget: registration is best-effort and must never block rendering.
    initPushRegistration(dispatch);
  }, [auth]);

  useEffect(() => {
    if (!auth) return undefined;
    // A notification tap deep-links to its document, whether the tap
    // happened while running (event) or launched the app (pending id
    // consumed after auth).
    const openPending = () => {
      const path = consumePendingPath();
      if (path) {
        navigate(path);
      }
    };
    openPending();
    window.addEventListener(OPEN_DOCUMENT_EVENT, openPending);
    return () => window.removeEventListener(OPEN_DOCUMENT_EVENT, openPending);
  }, [auth]);

  return auth ? (
    <ShepherdProvider>
      <Outlet />
      <NotificationPrimer />
      <RatingPrompt />
    </ShepherdProvider>
  ) : <Loader />;
};

export default PersistLogin;
